import { Component } from '@angular/core';
import { AccountScreen, AccountVestigingenRow, getAccountVestigingen } from '../account-screen';
import { createColumnDef, createModel, TableModel } from '../../shared/components/table/table/table.model';
import { combineLatest, Observable, of } from 'rxjs';
import { nonBreakingSpace } from '../../services/labels';
import { map, switchMap } from 'rxjs/operators';
import { SectieToekennenData, SectieToekennenDialogComponent } from '../../dialogs/account/sectie-toekennen-dialog/sectie-toekennen-dialog.component';
import { AsyncPipe, TitleCasePipe } from '@angular/common';
import { TableComponent } from '../../shared/components/table/table/table.component';
import { TooltipDirective } from '@cumlaude/shared-components-overlays';
import { ButtonComponent } from '@cumlaude/shared-components-buttons';
import { InstantSearchBoxComponent, MultiSelectDropdownComponent } from '@cumlaude/shared-components-inputs';
import { DimVvVestigingVak, LicentieRol, RCumLaudeAccount, RCumLaudeModule, RRol, RVestiging } from '@cumlaude/service-contract';
import { IconComponent } from '@cumlaude/shared-components-icons';

interface AccountSectieRow extends AccountVestigingenRow {
	autorisaties: string;
}

function makeRow(account: RCumLaudeAccount, alleVakken: DimVvVestigingVak[], alleVestigingen: RVestiging[]): AccountSectieRow {
	const autorisaties = account.vakken
		.map((vakId) => alleVakken.find((vak) => vak.vv_nm_vak === vakId)?.vv_nm_vak)
		.filter(Boolean)
		.join(', ');
	const vestigingen = getAccountVestigingen(account, alleVestigingen);
	return { account, autorisaties, vestigingen };
}

@Component({
	selector: 'app-sectie',
	templateUrl: '../account-screen.html',
	styleUrls: ['../account-screen.scss'],
	imports: [
		InstantSearchBoxComponent,
		ButtonComponent,
		MultiSelectDropdownComponent,
		TooltipDirective,
		TableComponent,
		AsyncPipe,
		TitleCasePipe,
		IconComponent,
	],
})
export class SectieComponent extends AccountScreen<AccountSectieRow> {
	override getRol(): RRol {
		return RRol.SECTIELEIDER;
	}

	override getModule(): RCumLaudeModule {
		return RCumLaudeModule.SECTIE;
	}

	override getRolLabel() {
		return 'Sectieleider';
	}

	override getLicentieRol(): LicentieRol {
		return LicentieRol.SECTIE;
	}

	override getRolDescription() {
		return 'Sectieleiders kunnen alle gegevens zien van de vakken op de vestiging waar zij aan gekoppeld zijn.';
	}

	makeAccountTableModel(accounts: RCumLaudeAccount[]): Observable<TableModel<AccountSectieRow>> {
		return combineLatest([this.dataService.getVestigingVakken(), this.alleVestigingen$]).pipe(
			map(([alleVakken, vestigingen]) => {
				const model = createModel(
					accounts.map((account) => makeRow(account, alleVakken, vestigingen)),
					(row) => row.account.id!
				);
				const bewerken = this.createActionColumn(
					'edit',
					'Rol bewerken'.replace(' ', nonBreakingSpace),
					'edit',
					({ account }) => this.openSectieToekennenDialog(account),
					() => true
				);
				model.columnDefs = [
					this.getNaamColumn(),
					createColumnDef('vestigingen', 'Vestigingen'),
					createColumnDef('autorisaties', 'Vakken'),
					bewerken,
					this.createRolIntrekkenColumn(),
				];
				return model;
			})
		);
	}

	override openRolToekennenDialog() {
		this.openSectieToekennenDialog();
	}

	openSectieToekennenDialog(account?: RCumLaudeAccount) {
		const allAccounts = account
			? of([account])
			: this.getVestigingen().pipe(
					switchMap((vestigingen) => this.restService.getAccountsFromFilter({ vestiging: vestigingen, excludeRol: RRol.SECTIELEIDER }))
				);
		combineLatest([allAccounts, this.dataService.getVestigingVakken()]).subscribe(([accounts, alleVakken]) => {
			const data: SectieToekennenData = {
				account,
				alleAccounts: accounts,
				alleVakken,
			};
			const dialogRef = this.dialog.open<RCumLaudeAccount>(SectieToekennenDialogComponent, { data });
			dialogRef.closed.subscribe((editedAccount) => {
				if (!editedAccount) return;
				this.resetFilter();
				editedAccount.rollen.push(this.getRol());
				this.accountsService.update$.next([
					editedAccount,
					() => {
						if (!account) this.accountsService.createLocal$.next(editedAccount);
					},
					() => {},
				]);
			});
		});
	}
}
